import { AccordionDetails, Box, Tooltip, useTheme } from '@mui/material';
import { CaretDown, Plus, Warning } from '@phosphor-icons/react';
import React, { useMemo, useState } from 'react';
import { useDarkMode } from '../../../../hooks/useDarkMode';
import { dropHighlightSx } from '../dnd/dropHighlightSx';
import type { RootDropData } from '../dnd/types';
import { useDropTargetNode } from '../dnd/useDropTargetNode';
import {
  SectionTitle,
  StyledAccordion,
  StyledAccordionSummary,
  sectionIconButtonSx,
} from '../sidebarSectionStyles';

interface SidebarSectionProps {
  title: string;
  section: 'agents' | 'data';
  spaceId?: string | null;
  hasBorder?: boolean;
  isAccordion?: boolean;
  defaultExpanded?: boolean;
  isDisabled?: boolean;
  onAddFolder?: () => void;
  rightSlot?: React.ReactNode;
  rightSlotForceVisible?: boolean;
  onContextMenu?: (event: React.MouseEvent) => void;
  contextMenu?: React.ReactNode;
  children: React.ReactNode;
}

export const SidebarSection: React.FC<SidebarSectionProps> = ({
  title,
  section,
  spaceId = null,
  hasBorder = false,
  isAccordion = false,
  defaultExpanded = false,
  isDisabled = false,
  onAddFolder,
  rightSlot,
  rightSlotForceVisible = false,
  onContextMenu,
  contextMenu,
  children,
}) => {
  const theme = useTheme();
  const palette = theme.customColorPalette;
  const isDarkMode = useDarkMode();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [isHovered, setIsHovered] = useState(false);
  const rootDropData = useMemo<RootDropData>(
    () => ({ kind: 'root-drop', spaceId, section }),
    [spaceId, section],
  );
  const rootDroppable = useDropTargetNode({
    data: rootDropData,
    mode: 'root',
  });
  const rootDropSx = dropHighlightSx(rootDroppable.isOver, palette.blue[500]);
  const iconBtnSx = sectionIconButtonSx(isDarkMode, palette);

  const addFolderButton = isHovered && onAddFolder && (
    <Box
      component='button'
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onAddFolder();
      }}
      sx={iconBtnSx}
    >
      <Plus
        size={16}
        color={isDarkMode ? palette.gray[400] : palette.gray[500]}
      />
    </Box>
  );

  const showRightSlot = isHovered || rightSlotForceVisible;
  const trailing = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        marginLeft: 'auto',
        flexShrink: 0,
      }}
    >
      {addFolderButton}
      {showRightSlot ? rightSlot : null}
    </Box>
  );

  if (isAccordion) {
    return (
      <>
        <Box>
          <StyledAccordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            hasBorder={hasBorder}
            TransitionProps={{ unmountOnExit: true }}
          >
            <StyledAccordionSummary
              onContextMenu={onContextMenu}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              expandIcon={
                <CaretDown
                  size={16}
                  color={palette.gray[500]}
                  style={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              }
            >
              <Box
                ref={rootDroppable.setRef}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  overflow: 'hidden',
                  maxWidth: '100%',
                  flex: 1,
                  minWidth: 0,
                  ...rootDropSx,
                }}
              >
                {isDisabled && (
                  <Tooltip
                    title='This space is disabled. Go to Settings to enable it.'
                    arrow
                    placement='top'
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Warning size={14} color='#FFC107' weight='fill' />
                    </Box>
                  </Tooltip>
                )}
                <Tooltip title={title} arrow placement='top'>
                  <SectionTitle sx={{ flex: 1, minWidth: 0 }}>
                    {title}
                  </SectionTitle>
                </Tooltip>
                {trailing}
              </Box>
            </StyledAccordionSummary>
            <AccordionDetails sx={{ padding: '0 8px 8px 8px' }}>
              {children}
            </AccordionDetails>
          </StyledAccordion>
        </Box>
        {contextMenu}
      </>
    );
  }

  return (
    <>
      <Box
        onContextMenu={onContextMenu}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          paddingBottom: '8px',
          marginBottom: '8px',
          borderBottom: hasBorder
            ? `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[200]}`
            : 'none',
        }}
      >
        <Box
          ref={rootDroppable.setRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 8px 4px 8px',
            ...rootDropSx,
          }}
        >
          <SectionTitle sx={{ flex: 1, minWidth: 0 }}>{title}</SectionTitle>
          {trailing}
        </Box>
        {children}
      </Box>
      {contextMenu}
    </>
  );
};
